import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import routes from "./routes.json";
import { organization } from "./schema";

export const SITE = "https://devis-to.be";
export const OG_IMAGE = `${SITE}/og-devisto.jpg`;
const PRIVATE_PREFIXES = ["/app", "/dashboard", "/login", "/register", "/settings", "/studio", "/admin", "/connexion", "/mot-de-passe-oublie", "/reinitialiser",
  "/devis/", "/facture/", "/payer/", "/contrat/", "/suivi-client/", "/avis/", "/rapport/", "/pt/", "/passeport/", "/demande/", "/maquette"];

const SELF_MANAGED = ["/belgique", "/france", "/espagne", "/luxembourg"];

export const isPrivatePath = (path) => PRIVATE_PREFIXES.some((p) => path === p || path.startsWith(p.endsWith("/") ? p : `${p}/`) || path.startsWith(p));
export const routeFor = (path) => routes.find((r) => r.path === path);

const defaultAlternates = (route, path) => {
  if (!route) return { fr: path, nl: null };
  if (route.lang === "nl") return { fr: route.fr || null, nl: route.path };
  return { fr: route.path, nl: route.nl || null };
};

/** Balises <head> d'une page : titre, description, robots, canonical, hreflang, Open Graph, Twitter et JSON-LD. */
export const Seo = ({ title, description, path, lang, robots, image = OG_IMAGE, type = "website", jsonLd = [], preloadImage, alternates, children }) => {
  const location = useLocation();
  const current = path || location.pathname.replace(/\/+$/, "") || "/";
  const route = routeFor(current);
  const noindex = robots ? robots.startsWith("noindex") : isPrivatePath(current);
  const finalTitle = title || route?.title || "Devisto — Logiciel de devis et facturation Peppol pour artisans";
  const finalDesc = description || route?.description || "Dictez la visite, Devisto fait le reste : devis rédigé par l'IA, facture Peppol, planning d'interventions. Pour artisans du bâtiment en Belgique.";
  const finalLang = lang || (route?.lang === "nl" ? "nl-BE" : "fr-BE");
  const alt = defaultAlternates(route, current);
  const url = `${SITE}${current === "/" ? "/" : current}`;
  const blocks = (Array.isArray(jsonLd) ? jsonLd : [jsonLd]).filter(Boolean);
  return (
    <Helmet prioritizeSeoTags>
      <html lang={finalLang} />
      <title>{finalTitle}</title>
      <meta name="description" content={finalDesc} />
      <meta name="robots" content={noindex ? "noindex,nofollow" : robots || "index,follow,max-image-preview:large"} />
      {!noindex && <link rel="canonical" href={url} />}
      {!noindex && alternates && alternates.map((a) => <link key={a.hrefLang} rel="alternate" hrefLang={a.hrefLang} href={a.href} />)}
      {!noindex && !alternates && alt.fr && <link rel="alternate" hrefLang="fr-BE" href={`${SITE}${alt.fr}`} />}
      {!noindex && !alternates && alt.nl && <link rel="alternate" hrefLang="nl-BE" href={`${SITE}${alt.nl}`} />}
      {!noindex && !alternates && <link rel="alternate" hrefLang="x-default" href={`${SITE}${alt.fr || current}`} />}
      {preloadImage && <link rel="preload" as="image" href={preloadImage} fetchPriority="high" />}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="Devisto" />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDesc} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content={finalLang === "nl-BE" ? "nl_BE" : "fr_BE"} />
      {alt.nl && alt.fr && <meta property="og:locale:alternate" content={finalLang === "nl-BE" ? "fr_BE" : "nl_BE"} />}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDesc} />
      <meta name="twitter:image" content={image} />
      {blocks.map((b, i) => <script key={i} type="application/ld+json">{JSON.stringify(b)}</script>)}
      {children}
    </Helmet>
  );
};

/** Filet de sécurité global : noindex automatique sur les routes privées, valeurs par défaut pour les pages publiques
 *  qui n'ont pas leur propre <Seo/> (les routes de routes.json en ont une). */
export const RouteSeo = () => {
  const { pathname } = useLocation();
  const path = pathname.replace(/\/+$/, "") || "/";
  if (routeFor(path) || SELF_MANAGED.some((p) => path === p || path.startsWith(`${p}/`))) return null;
  return <Seo path={path} jsonLd={isPrivatePath(path) ? [] : [organization()]} />;
};
